import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Languages, CheckCircle2, KeyRound, AlertCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ProviderInfo {
  id: string;
  label: string;
  needs_api_key: boolean;
  live: boolean;
}

interface TranslatorState {
  providers: ProviderInfo[];
  plan_matrix: Record<string, string[]>;
  defaults: Record<string, string>;
  fallback_enabled: boolean;
  api_keys_masked: Record<string, string>;
  api_keys_set: Record<string, boolean>;
  updated_at: string | null;
  updated_by: string | null;
}

const PLANS: { id: string; label: string }[] = [
  { id: "free", label: "Free" },
  { id: "starter", label: "Starter" },
  { id: "pro", label: "Pro" },
  { id: "business", label: "Business" },
];

export const TranslatorProvidersCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<TranslatorState>({
    queryKey: ["admin-translator"],
    queryFn: async () => (await api.get("/admin/translator")).data,
  });

  const [googleKey, setGoogleKey] = React.useState("");
  const [deeplKey, setDeeplKey] = React.useState("");

  const save = useMutation({
    mutationFn: async (payload: any) => (await api.put("/admin/translator", payload)).data,
    onSuccess: () => {
      toast.success("Translator settings saved");
      qc.invalidateQueries({ queryKey: ["admin-translator"] });
      setGoogleKey("");
      setDeeplKey("");
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const test = useMutation({
    mutationFn: async (provider: string) =>
      (await api.post("/admin/translator/test", {
        provider,
        text: "Hello, how are you today?",
        target: "Hindi",
      })).data,
    onSuccess: (d, provider) => {
      if (d.ok) {
        toast.success(`${provider}: ${d.result?.translated?.slice(0, 60) || "OK"}`);
      } else {
        toast.error(`${provider}: ${String(d.error).slice(0, 120)}`);
      }
    },
    onError: (e: any, provider) => toast.error(`${provider}: ${e?.response?.data?.detail || "Test failed"}`),
  });

  if (q.isLoading || !q.data) {
    return (
      <div className="sg-card p-6 flex items-center gap-2" data-testid="translator-card-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading translator config…
      </div>
    );
  }
  const s = q.data;

  const toggleProviderForPlan = (plan: string, provider: string) => {
    const cur = s.plan_matrix[plan] || [];
    const next = cur.includes(provider)
      ? cur.filter((p) => p !== provider)
      : [...cur, provider];
    // never leave a plan with an empty list
    if (next.length === 0) return toast.error("Each plan must allow at least one provider.");
    save.mutate({ plan_matrix: { ...s.plan_matrix, [plan]: next } });
  };

  return (
    <div className="sg-card p-6" data-testid="translator-providers-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
          <Languages className="h-5 w-5 text-violet-700" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold tracking-tight">
            Translator providers
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-1">
            Pick which translation engine each subscription tier can use.
            When multiple are allowed, the router tries the plan's default
            first and falls back through the list. New providers automatically
            unlock once their API key is saved below.
          </p>
        </div>
      </div>

      {/* Provider status grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 mb-6">
        {s.providers.map((p) => (
          <div
            key={p.id}
            data-testid={`provider-row-${p.id}`}
            className={`border rounded-lg px-3 py-2.5 flex items-start justify-between gap-2 ${
              p.live ? "border-emerald-200 bg-emerald-50/50" : "border-[color:var(--sg-border)] bg-white"
            }`}
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[color:var(--sg-fg)] truncate">{p.label}</div>
              <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 flex items-center gap-1.5">
                {p.live ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    <span className="text-emerald-700 font-medium">Live</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-3 w-3 text-[color:var(--sg-muted)]" />
                    <span>{p.needs_api_key ? "Awaiting API key" : "Not available"}</span>
                  </>
                )}
              </div>
            </div>
            {p.live && (
              <button
                type="button"
                data-testid={`test-${p.id}`}
                onClick={() => test.mutate(p.id)}
                disabled={test.isPending}
                className="text-[11px] font-medium text-violet-700 hover:underline shrink-0"
              >
                {test.variables === p.id && test.isPending ? "Testing…" : "Test"}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Plan matrix */}
      <div className="border border-[color:var(--sg-border)] rounded-lg overflow-hidden mb-6">
        <div className="grid" style={{ gridTemplateColumns: `140px repeat(${s.providers.length}, minmax(80px,1fr)) 160px` }}>
          <div className="bg-[color:var(--sg-muted-bg)] px-3 py-2 text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold">
            Plan
          </div>
          {s.providers.map((p) => (
            <div key={p.id} className="bg-[color:var(--sg-muted-bg)] px-2 py-2 text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold text-center">
              {p.id}
            </div>
          ))}
          <div className="bg-[color:var(--sg-muted-bg)] px-3 py-2 text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] font-semibold">
            Default
          </div>

          {PLANS.map((plan) => (
            <React.Fragment key={plan.id}>
              <div className="px-3 py-2.5 border-t border-[color:var(--sg-border)] font-semibold text-sm text-[color:var(--sg-fg)]">
                {plan.label}
              </div>
              {s.providers.map((p) => {
                const allowed = (s.plan_matrix[plan.id] || []).includes(p.id);
                return (
                  <div key={`${plan.id}-${p.id}`} className="px-2 py-2.5 border-t border-[color:var(--sg-border)] flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={allowed}
                      onChange={() => toggleProviderForPlan(plan.id, p.id)}
                      disabled={save.isPending}
                      data-testid={`matrix-${plan.id}-${p.id}`}
                      className="h-4 w-4 accent-[color:var(--sg-violet)] cursor-pointer"
                    />
                  </div>
                );
              })}
              <div className="px-2 py-1.5 border-t border-[color:var(--sg-border)] flex items-center">
                <select
                  value={s.defaults[plan.id] || (s.plan_matrix[plan.id] || [])[0]}
                  onChange={(e) => save.mutate({ defaults: { ...s.defaults, [plan.id]: e.target.value } })}
                  disabled={save.isPending}
                  data-testid={`default-${plan.id}`}
                  className="w-full h-8 px-1.5 text-xs rounded-md border border-[color:var(--sg-border)] bg-white"
                >
                  {(s.plan_matrix[plan.id] || []).map((pid) => (
                    <option key={pid} value={pid}>{pid}</option>
                  ))}
                </select>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Fallback + API keys */}
      <div className="grid md:grid-cols-2 gap-4 mb-2">
        <div className="border border-[color:var(--sg-border)] rounded-lg p-4 bg-white">
          <div className="text-xs uppercase tracking-widest text-[color:var(--sg-muted)] mb-2">
            Fallback behavior
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={s.fallback_enabled}
              onChange={(e) => save.mutate({ fallback_enabled: e.target.checked })}
              data-testid="fallback-toggle"
              className="h-4 w-4 accent-[color:var(--sg-violet)]"
            />
            <span className="text-sm text-[color:var(--sg-fg)]">
              Automatically fall back to the next allowed provider on failure
            </span>
          </label>
          <p className="text-[11px] text-[color:var(--sg-muted)] mt-2">
            Recommended. When ON, translations basically never fail — the router
            cascades through the plan's allowed providers until one succeeds.
          </p>
        </div>

        <div className="border border-[color:var(--sg-border)] rounded-lg p-4 bg-white">
          <div className="text-xs uppercase tracking-widest text-[color:var(--sg-muted)] mb-2 flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5" /> Platform API keys
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[color:var(--sg-fg)]">Google Cloud Translate</label>
              <div className="text-[11px] text-[color:var(--sg-muted)] mb-1">
                {s.api_keys_set.google
                  ? <>Saved: <b>{s.api_keys_masked.google}</b> — paste a new key to replace, or clear the field to remove.</>
                  : "Enable Google Translate for Pro/Business tiers by pasting a Cloud API key."}
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="google-key-input"
                  placeholder={s.api_keys_set.google ? "Paste new key to replace…" : "AIza…"}
                  value={googleKey}
                  onChange={(e) => setGoogleKey(e.target.value)}
                  className="flex-1"
                />
                <Button
                  data-testid="save-google-key"
                  disabled={save.isPending || googleKey === ""}
                  onClick={() => save.mutate({ api_keys: { google: googleKey } })}
                  size="sm"
                >
                  <Save className="h-3.5 w-3.5 mr-1" /> Save
                </Button>
                {s.api_keys_set.google && (
                  <Button
                    data-testid="clear-google-key"
                    variant="outline"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ api_keys: { google: "" } })}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[color:var(--sg-fg)]">DeepL</label>
              <div className="text-[11px] text-[color:var(--sg-muted)] mb-1">
                {s.api_keys_set.deepl
                  ? <>Saved: <b>{s.api_keys_masked.deepl}</b> — paste a new key to replace, or clear to remove.</>
                  : "Free keys end with :fx; auto-routes to api-free.deepl.com."}
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="deepl-key-input"
                  placeholder={s.api_keys_set.deepl ? "Paste new key to replace…" : "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx:fx"}
                  value={deeplKey}
                  onChange={(e) => setDeeplKey(e.target.value)}
                  className="flex-1"
                />
                <Button
                  data-testid="save-deepl-key"
                  disabled={save.isPending || deeplKey === ""}
                  onClick={() => save.mutate({ api_keys: { deepl: deeplKey } })}
                  size="sm"
                >
                  <Save className="h-3.5 w-3.5 mr-1" /> Save
                </Button>
                {s.api_keys_set.deepl && (
                  <Button
                    data-testid="clear-deepl-key"
                    variant="outline"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => save.mutate({ api_keys: { deepl: "" } })}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {s.updated_at && (
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-3">
          Last updated {new Date(s.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};
